import React, { useState, useEffect } from 'react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Select } from '../../ui/Select';
import { Badge } from '../../ui/Badge';
import { colors, agentThemes, spacing, borderRadius } from '../../../constants/theme';

interface Scenario {
  id: string;
  name: string;
  type: 'baseline' | 'optimistic' | 'pessimistic' | 'custom';
  description: string;
  assumptions: Assumption[];
  metrics: ScenarioMetrics;
  timeline: TimelineData[];
  risks: Risk[];
  opportunities: Opportunity[];
}

interface Assumption {
  id: string;
  category: 'market' | 'competition' | 'internal' | 'external';
  factor: string;
  value: number | string;
  impact: 'high' | 'medium' | 'low';
}

interface ScenarioMetrics {
  revenue: number;
  revenueGrowth: number;
  profit: number;
  profitMargin: number;
  marketShare: number;
  customerCount: number;
  averageTicket: number;
  conversionRate: number;
}

interface TimelineData {
  month: string;
  revenue: number;
  profit: number;
  customers: number;
}

interface Risk {
  id: string;
  description: string;
  probability: number;
  impact: number;
  mitigation?: string;
}

interface Opportunity {
  id: string;
  description: string;
  potential: number;
  feasibility: 'high' | 'medium' | 'low';
}

interface ScenarioCompareProps {
  storeId?: string;
  period?: 'quarterly' | 'yearly';
  onScenarioSelect?: (scenario: Scenario) => void;
}

const ScenarioCompare: React.FC<ScenarioCompareProps> = ({
  storeId = 'store001',
  period = 'quarterly',
  onScenarioSelect
}) => {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);
  const [compareMode, setCompareMode] = useState<'metrics' | 'timeline' | 'assumptions'>('metrics');
  const [loading, setLoading] = useState(true);

  // サンプルシナリオデータ生成
  const generateScenarios = (): Scenario[] => {
    return [
      {
        id: 'scenario1',
        name: '基準シナリオ',
        type: 'baseline',
        description: '現状のトレンドを維持した場合の予測',
        assumptions: [
          { id: 'a1', category: 'market', factor: '市場成長率', value: '3%', impact: 'medium' },
          { id: 'a2', category: 'competition', factor: '競合店舗数', value: '変化なし', impact: 'low' },
          { id: 'a3', category: 'internal', factor: '営業時間', value: '現状維持', impact: 'low' }
        ],
        metrics: {
          revenue: 450000000,
          revenueGrowth: 3.2,
          profit: 45000000,
          profitMargin: 10,
          marketShare: 15,
          customerCount: 125000,
          averageTicket: 3600,
          conversionRate: 68
        },
        timeline: [
          { month: '1月', revenue: 35000000, profit: 3500000, customers: 9800 },
          { month: '2月', revenue: 33000000, profit: 3300000, customers: 9200 },
          { month: '3月', revenue: 38000000, profit: 3800000, customers: 10500 },
          { month: '4月', revenue: 40000000, profit: 4000000, customers: 11000 }
        ],
        risks: [
          { id: 'r1', description: '新規競合店の出店', probability: 30, impact: 15 },
          { id: 'r2', description: '原材料費の上昇', probability: 50, impact: 10 }
        ],
        opportunities: [
          { id: 'o1', description: 'デジタル化推進による効率改善', potential: 5000000, feasibility: 'high' },
          { id: 'o2', description: 'PB商品の拡充', potential: 8000000, feasibility: 'medium' }
        ]
      },
      {
        id: 'scenario2',
        name: '楽観シナリオ',
        type: 'optimistic',
        description: '積極的な施策実施と市場環境改善を想定',
        assumptions: [
          { id: 'a4', category: 'market', factor: '市場成長率', value: '5%', impact: 'high' },
          { id: 'a5', category: 'internal', factor: 'マーケティング投資', value: '+30%', impact: 'high' },
          { id: 'a6', category: 'external', factor: '地域イベント', value: '増加', impact: 'medium' }
        ],
        metrics: {
          revenue: 520000000,
          revenueGrowth: 15.6,
          profit: 62400000,
          profitMargin: 12,
          marketShare: 18,
          customerCount: 145000,
          averageTicket: 3586,
          conversionRate: 72
        },
        timeline: [
          { month: '1月', revenue: 40000000, profit: 4800000, customers: 11000 },
          { month: '2月', revenue: 38000000, profit: 4560000, customers: 10500 },
          { month: '3月', revenue: 45000000, profit: 5400000, customers: 12400 },
          { month: '4月', revenue: 48000000, profit: 5760000, customers: 13200 }
        ],
        risks: [
          { id: 'r3', description: '投資回収の遅れ', probability: 20, impact: 20 },
          { id: 'r4', description: '人材不足', probability: 40, impact: 15 }
        ],
        opportunities: [
          { id: 'o3', description: '新規顧客層の開拓', potential: 15000000, feasibility: 'high' },
          { id: 'o4', description: 'EC事業の拡大', potential: 20000000, feasibility: 'medium' }
        ]
      },
      {
        id: 'scenario3',
        name: '悲観シナリオ',
        type: 'pessimistic',
        description: '競争激化と経済環境悪化を想定',
        assumptions: [
          { id: 'a7', category: 'market', factor: '市場成長率', value: '-2%', impact: 'high' },
          { id: 'a8', category: 'competition', factor: '競合店舗数', value: '+2店舗', impact: 'high' },
          { id: 'a9', category: 'external', factor: '消費者信頼感', value: '低下', impact: 'high' }
        ],
        metrics: {
          revenue: 380000000,
          revenueGrowth: -15.6,
          profit: 26600000,
          profitMargin: 7,
          marketShare: 12,
          customerCount: 105000,
          averageTicket: 3619,
          conversionRate: 62
        },
        timeline: [
          { month: '1月', revenue: 30000000, profit: 2100000, customers: 8300 },
          { month: '2月', revenue: 28000000, profit: 1960000, customers: 7700 },
          { month: '3月', revenue: 32000000, profit: 2240000, customers: 8800 },
          { month: '4月', revenue: 31000000, profit: 2170000, customers: 8500 }
        ],
        risks: [
          { id: 'r5', description: '大手チェーンの参入', probability: 60, impact: 30 },
          { id: 'r6', description: '不況による消費減退', probability: 40, impact: 25 }
        ],
        opportunities: [
          { id: 'o5', description: 'コスト削減施策', potential: 3000000, feasibility: 'high' },
          { id: 'o6', description: 'ニッチ市場への特化', potential: 5000000, feasibility: 'low' }
        ]
      }
    ];
  };

  useEffect(() => {
    setTimeout(() => {
      const data = generateScenarios();
      setScenarios(data);
      setSelectedScenarios([data[0].id, data[1].id]);
      setLoading(false);
    }, 800);
  }, [period, storeId]);

  const getScenarioColor = (type: Scenario['type']) => {
    const colorMap = {
      baseline: colors.accent.blue,
      optimistic: colors.status.success,
      pessimistic: colors.status.error,
      custom: '#8B4513'
    };
    return colorMap[type];
  };

  const formatCurrency = (amount: number) => {
    if (amount >= 100000000) {
      return `${(amount / 100000000).toFixed(1)}億円`;
    } else if (amount >= 10000000) {
      return `${(amount / 10000000).toFixed(0)}千万円`;
    } else if (amount >= 10000) {
      return `${(amount / 10000).toFixed(0)}万円`;
    }
    return `${amount.toLocaleString()}円`;
  };

  const renderComparisonChart = () => {
    const selected = scenarios.filter(s => selectedScenarios.includes(s.id));
    const maxRevenue = Math.max(...selected.flatMap(s => s.timeline.map(t => t.revenue)));
    
    return (
      <div style={{ 
        position: 'relative',
        height: '300px',
        backgroundColor: colors.background.primary,
        borderRadius: borderRadius.md,
        padding: spacing.lg
      }}>
        <svg width="100%" height="100%" viewBox="0 0 600 250">
          {/* Grid lines */}
          {[0, 1, 2, 3, 4].map(i => (
            <g key={i}>
              <line
                x1="50"
                y1={50 + i * 40}
                x2="550"
                y2={50 + i * 40}
                stroke={colors.support.gray}
                strokeWidth="1"
                strokeDasharray={i === 0 ? "0" : "2,2"}
              />
              <text
                x="40"
                y={55 + i * 40}
                fontSize="10"
                fill={colors.text.light}
                textAnchor="end"
              >
                {formatCurrency(maxRevenue * (1 - i * 0.25))}
              </text>
            </g>
          ))}

          {/* Timeline chart lines */}
          {selected.map((scenario, sIndex) => {
            const points = scenario.timeline.map((data, index) => {
              const x = 50 + (index * 500) / (scenario.timeline.length - 1);
              const y = 210 - (data.revenue / maxRevenue) * 160;
              return `${x},${y}`;
            }).join(' ');

            return (
              <g key={scenario.id}>
                <polyline
                  points={points}
                  stroke={getScenarioColor(scenario.type)}
                  strokeWidth="2"
                  fill="none"
                />
                {scenario.timeline.map((data, index) => {
                  const x = 50 + (index * 500) / (scenario.timeline.length - 1);
                  const y = 210 - (data.revenue / maxRevenue) * 160;
                  return (
                    <g key={index}>
                      <circle
                        cx={x}
                        cy={y}
                        r="4"
                        fill={getScenarioColor(scenario.type)}
                      />
                      {index === 0 && (
                        <text
                          x={x}
                          y="235"
                          fontSize="10"
                          fill={colors.text.light}
                          textAnchor="middle"
                        >
                          {data.month}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>
    );
  };

  return (
    <div style={{ padding: spacing.lg }}>
      {/* ヘッダー */}
      <div style={{ marginBottom: spacing.lg }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: spacing.md
        }}>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: 600, 
            color: colors.text.dark,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm
          }}>
            <span>{agentThemes.md.icon}</span>
            シナリオ比較分析
          </h2>
          <Select
            value={period}
            style={{ width: '150px' }}
          >
            <option value="quarterly">四半期</option>
            <option value="yearly">年間</option>
          </Select>
        </div>

      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ color: colors.text.light }}>シナリオを生成中...</div>
        </div>
      ) : (
        <>
          {/* シナリオ選択 */}
          <div style={{ 
            display: 'flex', 
            gap: spacing.md,
            marginBottom: spacing.lg
          }}>
            {scenarios.map(scenario => (
              <Card
                key={scenario.id}
                onClick={() => {
                  if (selectedScenarios.includes(scenario.id)) {
                    setSelectedScenarios(selectedScenarios.filter(id => id !== scenario.id));
                  } else {
                    setSelectedScenarios([...selectedScenarios, scenario.id]);
                  }
                  onScenarioSelect?.(scenario);
                }}
                style={{
                  flex: 1,
                  cursor: 'pointer',
                  border: selectedScenarios.includes(scenario.id) 
                    ? `2px solid ${getScenarioColor(scenario.type)}`
                    : `1px solid ${colors.support.gray}`,
                  transition: 'all 200ms ease'
                }}
              >
                <div style={{ padding: spacing.sm }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: spacing.xs
                  }}>
                    <h3 style={{ 
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: colors.text.dark
                    }}>
                      {scenario.name}
                    </h3>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: getScenarioColor(scenario.type)
                    }} />
                  </div>
                  <p style={{ 
                    fontSize: '0.75rem',
                    color: colors.text.light,
                    marginBottom: spacing.sm
                  }}>
                    {scenario.description}
                  </p>
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem'
                  }}>
                    <div>
                      <div style={{ color: colors.text.light, fontSize: '0.625rem' }}>売上予測</div>
                      <div style={{ 
                        fontWeight: 600,
                        color: colors.text.dark,
                        fontSize: '0.875rem'
                      }}>
                        {formatCurrency(scenario.metrics.revenue)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: colors.text.light, fontSize: '0.625rem' }}>成長率</div>
                      <div style={{ 
                        fontWeight: 600,
                        color: scenario.metrics.revenueGrowth > 0 
                          ? colors.status.success 
                          : colors.status.error,
                        fontSize: '0.875rem'
                      }}>
                        {scenario.metrics.revenueGrowth > 0 ? '+' : ''}{scenario.metrics.revenueGrowth}%
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* 比較コンテンツ - 全て表示 */}
          {/* 主要指標の比較 */}
          <Card>
              <div style={{ padding: spacing.lg }}>
                <h3 style={{ 
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: colors.text.dark,
                  marginBottom: spacing.lg
                }}>
                  主要指標の比較
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${colors.support.gray}` }}>
                        <th style={{ 
                          padding: spacing.md,
                          textAlign: 'left',
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: colors.text.dark
                        }}>
                          指標
                        </th>
                        {scenarios.filter(s => selectedScenarios.includes(s.id)).map(scenario => (
                          <th key={scenario.id} style={{ 
                            padding: spacing.md,
                            textAlign: 'right',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: colors.text.dark
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: spacing.xs }}>
                              <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: getScenarioColor(scenario.type)
                              }} />
                              {scenario.name}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { key: 'revenue', label: '売上高', format: (v: number) => formatCurrency(v) },
                        { key: 'revenueGrowth', label: '成長率', format: (v: number) => `${v > 0 ? '+' : ''}${v}%` },
                        { key: 'profit', label: '利益', format: (v: number) => formatCurrency(v) },
                        { key: 'profitMargin', label: '利益率', format: (v: number) => `${v}%` },
                        { key: 'marketShare', label: '市場シェア', format: (v: number) => `${v}%` },
                        { key: 'customerCount', label: '顧客数', format: (v: number) => v.toLocaleString() },
                        { key: 'averageTicket', label: '平均客単価', format: (v: number) => `¥${v.toLocaleString()}` },
                        { key: 'conversionRate', label: '転換率', format: (v: number) => `${v}%` }
                      ].map(metric => (
                        <tr key={metric.key} style={{ borderBottom: `1px solid ${colors.support.gray}` }}>
                          <td style={{ 
                            padding: spacing.md,
                            fontSize: '0.875rem',
                            color: colors.text.dark
                          }}>
                            {metric.label}
                          </td>
                          {scenarios.filter(s => selectedScenarios.includes(s.id)).map(scenario => {
                            const value = scenario.metrics[metric.key as keyof ScenarioMetrics];
                            return (
                              <td key={scenario.id} style={{ 
                                padding: spacing.md,
                                textAlign: 'right',
                                fontSize: '0.875rem',
                                fontWeight: 500
                              }}>
                                {metric.format(value as number)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>

          {/* タイムライン比較 */}
          <Card>
              <div style={{ padding: spacing.lg }}>
                <h3 style={{ 
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: colors.text.dark,
                  marginBottom: spacing.lg
                }}>
                  売上推移の比較
                </h3>
                {renderComparisonChart()}
                
                {/* 凡例 */}
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  gap: spacing.lg,
                  marginTop: spacing.lg
                }}>
                  {scenarios.filter(s => selectedScenarios.includes(s.id)).map(scenario => (
                    <div key={scenario.id} style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing.xs
                    }}>
                      <div style={{
                        width: '20px',
                        height: '3px',
                        backgroundColor: getScenarioColor(scenario.type)
                      }} />
                      <span style={{ fontSize: '0.875rem', color: colors.text.dark }}>
                        {scenario.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

          {/* 前提条件の比較 */}
          <div style={{ display: 'grid', gap: spacing.md }}>
              {scenarios.filter(s => selectedScenarios.includes(s.id)).map(scenario => (
                <Card key={scenario.id}>
                  <div style={{ padding: spacing.lg }}>
                    <h3 style={{ 
                      fontSize: '1.125rem',
                      fontWeight: 600,
                      color: colors.text.dark,
                      marginBottom: spacing.md,
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing.xs
                    }}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: getScenarioColor(scenario.type)
                      }} />
                      {scenario.name}の前提条件
                    </h3>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.lg }}>
                      {/* 前提条件 */}
                      <div>
                        <h4 style={{ 
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: colors.text.dark,
                          marginBottom: spacing.sm
                        }}>
                          主要前提
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                          {scenario.assumptions.map(assumption => (
                            <div key={assumption.id} style={{
                              padding: spacing.sm,
                              backgroundColor: colors.background.secondary,
                              borderRadius: borderRadius.sm,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <div>
                                <div style={{ fontSize: '0.875rem', color: colors.text.dark }}>
                                  {assumption.factor}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: colors.text.light }}>
                                  {assumption.value}
                                </div>
                              </div>
                              <Badge 
                                size="sm"
                                variant={
                                  assumption.impact === 'high' ? 'danger' :
                                  assumption.impact === 'medium' ? 'warning' :
                                  'secondary'
                                }
                              >
                                {assumption.impact}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* リスクと機会 */}
                      <div>
                        <h4 style={{ 
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: colors.text.dark,
                          marginBottom: spacing.sm
                        }}>
                          リスクと機会
                        </h4>
                        <div style={{ marginBottom: spacing.md }}>
                          <div style={{ fontSize: '0.75rem', color: colors.text.light, marginBottom: spacing.xs }}>
                            主要リスク
                          </div>
                          {scenario.risks.map(risk => (
                            <div key={risk.id} style={{
                              fontSize: '0.875rem',
                              color: colors.status.error,
                              marginBottom: spacing.xs
                            }}>
                              • {risk.description} ({risk.probability}%)
                            </div>
                          ))}
                        </div>
                        <div>
                          <div style={{ fontSize: '0.75rem', color: colors.text.light, marginBottom: spacing.xs }}>
                            機会
                          </div>
                          {scenario.opportunities.map(opp => (
                            <div key={opp.id} style={{
                              fontSize: '0.875rem',
                              color: colors.status.success,
                              marginBottom: spacing.xs
                            }}>
                              • {opp.description}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
        </>
      )}
    </div>
  );
};

export default ScenarioCompare;